/**
 * Paginator — Measures rendered content and splits it across fixed A4 sheets.
 *
 * Renders children into a hidden measurer div at A4 width, walks top-level
 * child elements measuring their heights, and buckets them into pages.
 * Each page gets a header and footer. Tables that span multiple pages
 * get their <thead> repeated on continuation sheets. Long symptoms lists
 * are split row-by-row across pages.
 *
 * Usage:
 *   <PaginatedContent
 *     header={<PageHeader ... />}
 *     footer={<PageFooter ... />}
 *     padding="15mm"
 *   >
 *     <h2>Title</h2>
 *     <table>...could be huge...</table>
 *     <div>More stuff</div>
 *   </PaginatedContent>
 */

import { useEffect, useRef, useState } from "react";

// A4 in px at 96dpi: 210mm = 793.7px, 297mm = 1122.52px
// But we measure in the actual rendered DOM so units don't matter —
// we just compare against the measurer's clientHeight.

// Padding in mm -> px (approximate at 96dpi: 1mm ≈ 3.7795px)
const MM_TO_PX = 3.7795;

function parsePaddingMm(padding) {
  const match = padding?.match(/(\d+)mm/);
  return match ? parseInt(match[1], 10) : 15;
}

/**
 * Is this element a heading (h1-h6)?
 */
function isHeading(el) {
  return /^H[1-6]$/.test(el.tagName);
}

/**
 * Is this element a section label badge?
 */
function isSectionLabel(el) {
  return el.classList?.contains("marker-section-badge");
}

/**
 * Is this the markers section title block?
 */
function isMarkersHeader(el) {
  return el.classList?.contains("result-markers-header");
}

/**
 * Should this element be kept with the element that follows it?
 */
function keepsWithNext(el) {
  return isHeading(el) || isSectionLabel(el) || isMarkersHeader(el);
}

/**
 * Is this the stacked potential symptoms list?
 */
function isSymptomsList(el) {
  return el.classList?.contains("symptoms-list");
}

/**
 * Height of a symptoms list's first row — used for page-break decisions
 * so a long list doesn't force its section badge onto the next page.
 */
function symptomsListStartHeight(listEl) {
  const firstRow = listEl.querySelector(".symptom-row");
  return firstRow ? firstRow.offsetHeight : 0;
}

/**
 * Height of a table's thead plus first body row — used for page-break decisions
 * so a long table doesn't force its section badge onto the next page.
 */
function tableStartHeight(tableEl) {
  const thead = tableEl.querySelector("thead");
  const theadHeight = thead ? thead.offsetHeight : 0;
  const firstRow = tableEl.querySelector("tbody > tr");
  const firstRowHeight = firstRow ? firstRow.offsetHeight : 0;
  return theadHeight + firstRowHeight;
}

/**
 * Combined height for keep-with-next checks (badge + table start, header + badge + table start, etc.)
 */
function getKeepWithNextHeight(el, elements, index) {
  const nextEl = elements[index + 1];
  if (!nextEl) return el.offsetHeight;

  if (isSectionLabel(nextEl) && elements[index + 2]) {
    if (elements[index + 2].tagName === "TABLE") {
      return el.offsetHeight + nextEl.offsetHeight + tableStartHeight(elements[index + 2]);
    }
    if (isSymptomsList(elements[index + 2])) {
      return el.offsetHeight + nextEl.offsetHeight + symptomsListStartHeight(elements[index + 2]);
    }
  }

  if (nextEl.tagName === "TABLE") {
    return el.offsetHeight + tableStartHeight(nextEl);
  }

  if (isSymptomsList(nextEl)) {
    return el.offsetHeight + symptomsListStartHeight(nextEl);
  }

  return el.offsetHeight + nextEl.offsetHeight;
}

/**
 * Given a list of DOM elements and an available height budget,
 * split them into pages. If a <table> doesn't fit, split it row-by-row.
 * Headings are kept with the element that follows them.
 */
function splitIntoPages(elements, availableHeight) {
  const pages = [];
  let currentPage = [];
  let currentHeight = 0;

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const elHeight = el.offsetHeight;

    // If this is a heading or section badge, measure it together with the next element
    // so the label never ends up orphaned at the bottom of a page.
    if (keepsWithNext(el) && i + 1 < elements.length) {
      const combinedHeight = getKeepWithNextHeight(el, elements, i);

      // If the heading + next element won't fit, start a new page
      if (currentHeight + combinedHeight > availableHeight && currentPage.length > 0) {
        const lastItem = currentPage[currentPage.length - 1];
        if (lastItem?.type === "element" && isMarkersHeader(lastItem.el)) {
          const headerItem = currentPage.pop();
          currentHeight -= headerItem.el.offsetHeight;
          if (currentPage.length > 0) {
            pages.push(currentPage);
          }
          currentPage = [headerItem];
          currentHeight = headerItem.el.offsetHeight;
        } else {
          pages.push(currentPage);
          currentPage = [];
          currentHeight = 0;
        }
      }

      // Add the heading to the current page — the next element will be
      // handled in the next iteration (it may still need table-splitting)
      currentPage.push({ type: "element", el });
      currentHeight += elHeight;
      continue;
    }

    // If it's a table and doesn't fit, split by rows
    if (el.tagName === "TABLE" && elHeight > availableHeight - currentHeight) {
      const thead = el.querySelector("thead");
      const theadHeight = thead ? thead.offsetHeight : 0;
      const rows = Array.from(el.querySelectorAll("tbody > tr"));

      let tableRows = [];
      // First chunk shares the page with whatever's already there (e.g. the heading)
      let tableHeight = currentHeight + theadHeight;
      let isFirstChunk = true;

      for (const row of rows) {
        const rowHeight = row.offsetHeight;

        if (tableHeight + rowHeight > availableHeight && tableRows.length > 0) {
          if (isFirstChunk) {
            // First chunk — append to current page (keeps heading with table start)
            currentPage.push({ type: "table-chunk", thead, rows: tableRows });
            pages.push(currentPage);
            currentPage = [];
            isFirstChunk = false;
          } else {
            pages.push([{ type: "table-chunk", thead, rows: tableRows }]);
          }
          tableRows = [];
          tableHeight = theadHeight;
        }

        tableRows.push(row);
        tableHeight += rowHeight;
      }

      // Remaining rows go into current page
      if (tableRows.length > 0) {
        if (isFirstChunk) {
          currentPage.push({ type: "table-chunk", thead, rows: tableRows });
          currentHeight = tableHeight;
        } else {
          currentPage.push({ type: "table-chunk", thead, rows: tableRows });
          currentHeight = tableHeight;
        }
      }

      continue;
    }

    // If it's a symptoms list and doesn't fit, split by rows
    if (isSymptomsList(el) && elHeight > availableHeight - currentHeight) {
      const rows = Array.from(el.querySelectorAll(".symptom-row"));

      let listRows = [];
      let listHeight = currentHeight;
      let isFirstChunk = true;

      for (const row of rows) {
        const rowHeight = row.offsetHeight;

        if (listHeight + rowHeight > availableHeight && listRows.length > 0) {
          if (isFirstChunk) {
            currentPage.push({ type: "symptoms-list-chunk", rows: listRows });
            pages.push(currentPage);
            currentPage = [];
            isFirstChunk = false;
          } else {
            pages.push([{ type: "symptoms-list-chunk", rows: listRows }]);
          }
          listRows = [];
          listHeight = 0;
        }

        listRows.push(row);
        listHeight += rowHeight;
      }

      if (listRows.length > 0) {
        if (isFirstChunk) {
          currentPage.push({ type: "symptoms-list-chunk", rows: listRows });
          currentHeight = listHeight;
        } else {
          currentPage.push({ type: "symptoms-list-chunk", rows: listRows });
          currentHeight = listHeight;
        }
      }

      continue;
    }

    // Non-table element or table that fits
    if (currentHeight + elHeight > availableHeight && currentPage.length > 0) {
      pages.push(currentPage);
      currentPage = [];
      currentHeight = 0;
    }

    currentPage.push({ type: "element", el });
    currentHeight += elHeight;
  }

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
}

/**
 * Render a page item (either a cloned element or a reconstructed table chunk)
 */
function PageItem({ item, index }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    if (item.type === "table-chunk") {
      // Build a new table from the thead + row subset
      const table = document.createElement("table");
      table.className = "markers-table";
      if (item.thead) {
        table.appendChild(item.thead.cloneNode(true));
      }
      const tbody = document.createElement("tbody");
      item.rows.forEach(row => tbody.appendChild(row.cloneNode(true)));
      table.appendChild(tbody);
      ref.current.innerHTML = "";
      ref.current.appendChild(table);
    } else if (item.type === "symptoms-list-chunk") {
      const list = document.createElement("div");
      list.className = "symptoms-list";
      item.rows.forEach(row => list.appendChild(row.cloneNode(true)));
      ref.current.innerHTML = "";
      ref.current.appendChild(list);
    } else {
      ref.current.innerHTML = "";
      ref.current.appendChild(item.el.cloneNode(true));
    }
  }, [item]);

  return <div ref={ref} key={index} className="paginator-page-item" />;
}

export default function PaginatedContent({
  children,
  header,
  footer,
  padding = "15mm",
  sheetClassName = "",
  sectionAttr,
  sectionType,
  sectionId,
  tabs,
  onReady
}) {
  const measurerRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const [pages, setPages] = useState(null);

  useEffect(() => {
    if (!measurerRef.current) return;

    // Wait a frame for fonts/images to settle
    const raf = requestAnimationFrame(() => {
      const measurer = measurerRef.current;
      if (!measurer) return;

      // A4 = 297mm. Subtract everything that isn't content:
      //   sheet padding:  15mm top + 15mm bottom = 30mm
      //   content padding: 10mm top + 10mm bottom = 20mm
      //   footer:         ~15mm (abs positioned, 5mm from bottom + content)
      //   breathing room: 5mm
      // Total reserved:   ~70mm → usable content height ≈ 227mm
      const availableHeight = 227 * MM_TO_PX;

      // Get all direct children of the content area
      const contentEl = measurer.querySelector("[data-paginator-content]");
      if (!contentEl) return;

      const elements = Array.from(contentEl.children);
      const result = splitIntoPages(elements, availableHeight);
      setPages(result);
    });

    return () => cancelAnimationFrame(raf);
  }, [children, padding]);

  useEffect(() => {
    if (!pages || !onReady) return undefined;

    let cancelled = false;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) onReady();
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [pages, onReady]);

  const paddingMm = parsePaddingMm(padding);

  return (
    <>
      {/* Hidden measurer — renders at A4 width to get real measurements */}
      <div
        ref={measurerRef}
        className={`paginator-measurer no-print ${sheetClassName}`}
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          visibility: "hidden",
          padding: `${paddingMm}mm`,
          width: "210mm",
          height: "297mm",
        }}
      >
        <div ref={headerRef}>{header}</div>
        <div data-paginator-content>{children}</div>
        <div ref={footerRef}>{footer}</div>
      </div>

      {/* Rendered pages */}
      {pages
        ? pages.map((pageItems, pageIdx) => (
            <div
              key={pageIdx}
              className={`sheet ${sheetClassName}`}
              style={{ padding: `${paddingMm}mm` }}
              {...(sectionAttr ? { "data-section": sectionAttr } : {})}
              {...(sectionType ? { "data-section-type": pageIdx === 0 ? sectionType : `${sectionType}-cont` } : {})}
              {...(sectionId && pageIdx === 0 ? { id: sectionId } : {})}
            >
              {tabs}
              {header}
              <div className="paginated-content-body">
                {pageItems.map((item, itemIdx) => (
                  <PageItem key={itemIdx} item={item} index={itemIdx} />
                ))}
              </div>
              {footer}
            </div>
          ))
        : // Fallback while measuring — hidden from print; replaced once pagination completes
          <div className={`sheet paginator-fallback no-print ${sheetClassName}`} style={{ padding: `${paddingMm}mm` }}>
            {tabs}
            {header}
            <div className="paginated-content-body">
              {children}
            </div>
            {footer}
          </div>
      }
    </>
  );
}
